import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useAuth } from "../context/auth";

type RetryPolicy = {
	maxRetries: number | null;
	timeoutMs: number | null;
	disabled: boolean;
} | null;

type Endpoint = {
	id: string;
	url: string;
	events: string[];
	active: boolean;
	metadata: string | null;
	retryPolicy: RetryPolicy;
	createdAt: string;
	updatedAt: string;
};

const EVENT_TYPES = [
	"*",
	"webhook.endpoint.created",
	"webhook.endpoint.updated",
	"webhook.endpoint.deleted",
	"webhook.delivery.failed",
	"webhook.test.requested",
] as const;

function formatDate(dateStr: string): string {
	return new Date(dateStr).toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
	});
}

function errorMessageForStatus(status: number): string {
	switch (status) {
		case 422:
			return "Retry policy exceeds your plan tier limits.";
		case 429:
			return "Endpoint limit reached for your plan. Upgrade to add more.";
		case 404:
			return "Endpoint not found. It may have been deleted.";
		case 401:
			return "Session expired. Please sign in again.";
		case 403:
			return "You don't have permission to perform this action.";
		default:
			return `Something went wrong (${status}). Please try again.`;
	}
}

export function WebhooksPage() {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const { hasPermission } = useAuth();

	const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	const [url, setUrl] = useState("");
	const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
	const [maxRetriesInput, setMaxRetriesInput] = useState("");
	const [timeoutMsInput, setTimeoutMsInput] = useState("");
	const [retriesDisabled, setRetriesDisabled] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [newSecret, setNewSecret] = useState<string | null>(null);
	const [rotatedSecret, setRotatedSecret] = useState<{
		endpointId: string;
		secret: string;
	} | null>(null);

	const fetchEndpoints = useCallback(
		async (signal?: AbortSignal) => {
			try {
				setError(null);
				const res = await fetch(`/api/tenant/${workspaceSlug}/webhooks`, {
					credentials: "include",
					signal,
				});
				if (!res.ok) {
					throw new Error(errorMessageForStatus(res.status));
				}
				const data = (await res.json()) as { endpoints: Endpoint[] };
				setEndpoints(data.endpoints);
			} catch (err) {
				if (err instanceof DOMException && err.name === "AbortError") return;
				setError(
					err instanceof Error ? err.message : "Failed to load endpoints",
				);
			} finally {
				setLoading(false);
			}
		},
		[workspaceSlug],
	);

	useEffect(() => {
		const controller = new AbortController();
		fetchEndpoints(controller.signal);
		return () => controller.abort();
	}, [fetchEndpoints]);

	async function handleCreate(e: { preventDefault: () => void }) {
		e.preventDefault();
		setSubmitting(true);
		setError(null);
		setNewSecret(null);

		try {
			const retryPolicy: Record<string, number> = {};
			if (retriesDisabled) {
				retryPolicy.maxRetries = 0;
			} else if (maxRetriesInput !== "") {
				retryPolicy.maxRetries = Number(maxRetriesInput);
			}
			if (timeoutMsInput !== "") {
				retryPolicy.timeoutMs = Number(timeoutMsInput);
			}

			const body: Record<string, unknown> = {
				url,
				events: selectedEvents,
			};
			if (Object.keys(retryPolicy).length > 0) {
				body.retryPolicy = retryPolicy;
			}

			const res = await fetch(`/api/tenant/${workspaceSlug}/webhooks`, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(body),
			});
			if (!res.ok) {
				throw new Error(errorMessageForStatus(res.status));
			}
			const data = (await res.json()) as Endpoint & {
				signingSecret: string;
			};
			setNewSecret(data.signingSecret);
			setUrl("");
			setSelectedEvents([]);
			setMaxRetriesInput("");
			setTimeoutMsInput("");
			setRetriesDisabled(false);
			await fetchEndpoints();
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to create endpoint",
			);
		} finally {
			setSubmitting(false);
		}
	}

	async function handleToggle(endpoint: Endpoint) {
		setError(null);
		try {
			const res = await fetch(
				`/api/tenant/${workspaceSlug}/webhooks/${endpoint.id}`,
				{
					method: "PATCH",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ active: !endpoint.active }),
				},
			);
			if (!res.ok) {
				throw new Error(errorMessageForStatus(res.status));
			}
			await fetchEndpoints();
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to update endpoint",
			);
		}
	}

	async function handleRotateSecret(id: string) {
		setError(null);
		setRotatedSecret(null);
		try {
			const res = await fetch(
				`/api/tenant/${workspaceSlug}/webhooks/${id}/rotate-secret`,
				{
					method: "POST",
					credentials: "include",
				},
			);
			if (!res.ok) {
				throw new Error(errorMessageForStatus(res.status));
			}
			const data = (await res.json()) as { signingSecret: string };
			setRotatedSecret({ endpointId: id, secret: data.signingSecret });
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to rotate secret");
		}
	}

	async function handleDelete(id: string) {
		if (!window.confirm("Delete this webhook endpoint? This cannot be undone."))
			return;
		setError(null);
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/webhooks/${id}`, {
				method: "DELETE",
				credentials: "include",
			});
			if (!res.ok) {
				throw new Error(errorMessageForStatus(res.status));
			}
			setRotatedSecret(null);
			await fetchEndpoints();
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to delete endpoint",
			);
		}
	}

	function handleEventToggle(event: string) {
		setSelectedEvents((prev) =>
			prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event],
		);
	}

	if (!hasPermission("webhooks", "read")) {
		return (
			<div className="max-w-2xl mx-auto mt-8">
				<p className="text-sm text-gray-600" data-testid="webhooks-no-access">
					You don't have access to this page.
				</p>
			</div>
		);
	}

	return (
		<div className="max-w-3xl mx-auto mt-8" data-testid="webhooks-page">
			<h1 className="text-2xl font-bold text-gray-900 mb-6">Webhooks</h1>

			{error && (
				<div
					className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3 mb-4"
					data-testid="webhooks-error"
				>
					{error}
				</div>
			)}

			{newSecret && (
				<div
					className="bg-green-50 border border-green-200 rounded-md p-3 mb-4"
					data-testid="secret-container"
				>
					<p className="text-xs font-medium text-green-800 mb-1">
						This secret won't be shown again. Copy it now.
					</p>
					<input
						type="text"
						readOnly
						value={newSecret}
						onClick={(e) => (e.target as HTMLInputElement).select()}
						className="w-full text-sm bg-white border border-green-300 rounded px-2 py-1 font-mono text-green-900 focus:outline-none"
						data-testid="secret-input"
					/>
				</div>
			)}

			<section className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
				<h2 className="text-lg font-semibold text-gray-900 mb-4">Endpoints</h2>

				{loading ? (
					<div className="text-sm text-gray-500" data-testid="webhooks-loading">
						Loading endpoints...
					</div>
				) : endpoints.length === 0 ? (
					<p className="text-sm text-gray-400 text-center py-6">
						No webhook endpoints configured.
					</p>
				) : (
					<table className="w-full text-sm text-left">
						<thead>
							<tr className="border-b border-gray-200 text-gray-500 text-xs uppercase tracking-wider">
								<th className="py-2 pr-4 font-medium">URL</th>
								<th className="py-2 pr-4 font-medium">Events</th>
								<th className="py-2 pr-4 font-medium">Active</th>
								<th className="py-2 pr-4 font-medium">Retry Policy</th>
								<th className="py-2 pr-4 font-medium">Created</th>
								<th className="py-2 font-medium">Actions</th>
							</tr>
						</thead>
						<tbody>
							{endpoints.map((ep) => (
								<tr
									key={ep.id}
									className="border-b border-gray-100"
									data-testid="endpoint-row"
								>
									<td className="py-3 pr-4 text-gray-900 font-mono text-xs break-all">
										{ep.url}
									</td>
									<td className="py-3 pr-4 text-gray-600 text-xs">
										{ep.events.join(", ")}
									</td>
									<td className="py-3 pr-4">
										<span
											className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
												ep.active
													? "bg-green-100 text-green-800"
													: "bg-gray-100 text-gray-600"
											}`}
										>
											{ep.active ? "Active" : "Inactive"}
										</span>
									</td>
									<td className="py-3 pr-4 text-gray-500 text-xs">
										{ep.retryPolicy?.disabled
											? "Disabled"
											: ep.retryPolicy
												? `Custom (max: ${ep.retryPolicy.maxRetries ?? "default"}${ep.retryPolicy.timeoutMs ? `, timeout: ${ep.retryPolicy.timeoutMs}ms` : ""})`
												: "Plan default"}
									</td>
									<td className="py-3 pr-4 text-gray-500 text-xs">
										{formatDate(ep.createdAt)}
									</td>
									<td className="py-3">
										<div className="flex items-center gap-2">
											<Link
												to={`/${workspaceSlug}/settings/webhooks/${ep.id}/deliveries`}
												className="text-blue-600 hover:text-blue-800 text-xs"
												data-testid="view-deliveries-link"
											>
												View Deliveries
											</Link>
											{hasPermission("webhooks", "update") && (
												<>
													<button
														type="button"
														onClick={() => handleToggle(ep)}
														className="text-blue-600 hover:text-blue-800 text-xs cursor-pointer"
														data-testid="toggle-active-btn"
													>
														{ep.active ? "Disable" : "Enable"}
													</button>
													<button
														type="button"
														onClick={() => handleRotateSecret(ep.id)}
														className="text-blue-600 hover:text-blue-800 text-xs cursor-pointer"
														data-testid="rotate-secret-btn"
													>
														Rotate Secret
													</button>
												</>
											)}
											{hasPermission("webhooks", "delete") && (
												<button
													type="button"
													onClick={() => handleDelete(ep.id)}
													className="text-red-600 hover:text-red-800 text-xs cursor-pointer"
													data-testid="delete-endpoint-btn"
												>
													Delete
												</button>
											)}
										</div>
										{rotatedSecret?.endpointId === ep.id && (
											<div
												className="bg-green-50 border border-green-200 rounded-md p-2 mt-2"
												data-testid="secret-container"
											>
												<p className="text-xs font-medium text-green-800 mb-1">
													This secret won't be shown again. Copy it now.
												</p>
												<input
													type="text"
													readOnly
													value={rotatedSecret.secret}
													onClick={(e) =>
														(e.target as HTMLInputElement).select()
													}
													className="w-full text-xs bg-white border border-green-300 rounded px-2 py-1 font-mono text-green-900 focus:outline-none"
													data-testid="rotated-secret-input"
												/>
											</div>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</section>

			{hasPermission("webhooks", "create") && (
				<section className="bg-white border border-gray-200 rounded-lg p-6">
					<h2 className="text-lg font-semibold text-gray-900 mb-4">
						Add Endpoint
					</h2>
					<form onSubmit={handleCreate} data-testid="create-endpoint-form">
						<div className="mb-4">
							<label
								htmlFor="webhook-url"
								className="block text-xs font-medium text-gray-600 mb-1"
							>
								Endpoint URL
							</label>
							<input
								id="webhook-url"
								type="url"
								required
								value={url}
								onChange={(e) => setUrl(e.target.value)}
								placeholder="https://example.com/webhooks"
								className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
								data-testid="webhook-url-input"
							/>
						</div>
						<fieldset className="mb-4">
							<legend className="block text-xs font-medium text-gray-600 mb-2">
								Events
							</legend>
							<div className="flex flex-wrap gap-3">
								{EVENT_TYPES.map((event) => (
									<label
										key={event}
										className="flex items-center gap-1.5 text-sm text-gray-700"
									>
										<input
											type="checkbox"
											checked={selectedEvents.includes(event)}
											onChange={() => handleEventToggle(event)}
											className="rounded border-gray-300"
											data-testid={`event-checkbox-${event}`}
										/>
										<span className="font-mono text-xs">{event}</span>
									</label>
								))}
							</div>
						</fieldset>
						<fieldset className="mb-4">
							<legend className="block text-xs font-medium text-gray-600 mb-2">
								Retry Policy
							</legend>
							<div className="space-y-3">
								<label className="flex items-center gap-1.5 text-sm text-gray-700">
									<input
										type="checkbox"
										checked={retriesDisabled}
										onChange={() => setRetriesDisabled((v) => !v)}
										className="rounded border-gray-300"
										data-testid="retries-disabled-checkbox"
									/>
									<span className="text-xs">Disable retries</span>
								</label>
								{!retriesDisabled && (
									<div>
										<label
											htmlFor="max-retries"
											className="block text-xs font-medium text-gray-600 mb-1"
										>
											Max retries
										</label>
										<input
											id="max-retries"
											type="number"
											min={0}
											max={100}
											value={maxRetriesInput}
											onChange={(e) => setMaxRetriesInput(e.target.value)}
											placeholder="Plan default"
											className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
											data-testid="max-retries-input"
										/>
									</div>
								)}
								<div>
									<label
										htmlFor="timeout-ms"
										className="block text-xs font-medium text-gray-600 mb-1"
									>
										Timeout (ms)
									</label>
									<input
										id="timeout-ms"
										type="number"
										min={1000}
										max={30000}
										value={timeoutMsInput}
										onChange={(e) => setTimeoutMsInput(e.target.value)}
										placeholder="10000"
										className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
										data-testid="timeout-ms-input"
									/>
								</div>
								<p className="text-xs text-gray-400">
									Limited by your plan tier.
								</p>
							</div>
						</fieldset>
						<button
							type="submit"
							disabled={submitting || selectedEvents.length === 0}
							className="bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 cursor-pointer"
							data-testid="create-endpoint-btn"
						>
							{submitting ? "Creating..." : "Create Endpoint"}
						</button>
					</form>
				</section>
			)}
		</div>
	);
}
